<template>
  <el-select v-model="value" @change="handleChange" class="w-full" :placeholder="t('common.select_country_code')" filterable>
    <template #prefix>
      <span v-if="selectedCountry" class="mr-1 text-xl leading-none">{{ selectedCountry.flag }}</span>
    </template>
    <el-option v-for="country in sortedCountries" :key="country.dialCode" :label="`${country.name} (${country.dialCode})`" :value="country.dialCode">
      <div class="flex items-center h-[50px]">
        <span class="mr-2 text-2xl leading-none">{{ country.flag }}</span>
        <span class="text-xl leading-none">{{ country.name }} ({{ country.dialCode }})</span>
      </div>
    </el-option>
  </el-select>
</template>

<script lang="ts" setup>
import { ref, computed, unref } from 'vue';
import { useI18n } from 'vue-i18n';

const { t, locale } = useI18n();

interface CountryCode {
  name: string;
  dialCode: string;
  flag: string;
}

// Common country codes sorted by region relevance
const countryCodes: CountryCode[] = [
  { name: 'Singapore', dialCode: '+65', flag: '🇸🇬' },
  { name: 'Malaysia', dialCode: '+60', flag: '🇲🇾' },
  { name: 'Indonesia', dialCode: '+62', flag: '🇮🇩' },
  { name: 'Thailand', dialCode: '+66', flag: '🇹🇭' },
  { name: 'Vietnam', dialCode: '+84', flag: '🇻🇳' },
  { name: 'Philippines', dialCode: '+63', flag: '🇵🇭' },
  { name: 'China', dialCode: '+86', flag: '🇨🇳' },
  { name: 'Hong Kong', dialCode: '+852', flag: '🇭🇰' },
  { name: 'Taiwan', dialCode: '+886', flag: '🇹🇼' },
  { name: 'Japan', dialCode: '+81', flag: '🇯🇵' },
  { name: 'South Korea', dialCode: '+82', flag: '🇰🇷' },
  { name: 'India', dialCode: '+91', flag: '🇮🇳' },
  { name: 'Australia', dialCode: '+61', flag: '🇦🇺' },
  { name: 'New Zealand', dialCode: '+64', flag: '🇳🇿' },
  { name: 'United States', dialCode: '+1', flag: '🇺🇸' },
  { name: 'United Kingdom', dialCode: '+44', flag: '🇬🇧' },
  { name: 'Germany', dialCode: '+49', flag: '🇩🇪' },
  { name: 'France', dialCode: '+33', flag: '🇫🇷' },
  { name: 'UAE', dialCode: '+971', flag: '🇦🇪' },
  { name: 'Saudi Arabia', dialCode: '+966', flag: '🇸🇦' },
];

const $props = defineProps<{ modelValue?: string }>();
const $emit = defineEmits(['change', 'update:modelValue']);
const value = ref($props.modelValue || '+65');

const sortedCountries = computed(() => [...countryCodes].sort((a, b) => a.name.localeCompare(b.name)));
const selectedCountry = computed(() => countryCodes.find(c => c.dialCode === value.value));

const handleChange = () => {
  $emit('change', value.value);
  $emit('update:modelValue', value.value);
};
</script>

<style lang="scss" scoped>
:deep(.el-select-dropdown__item) {
  padding: 0 12px;
  height: 50px;
  line-height: 50px;
}
</style>
